<script setup>
import {ref, onMounted, onUnmounted, computed} from 'vue';
import {useWindowSize} from '@vueuse/core';
import {loadImage} from '~/helpers/imageCache';
import FirstImage from '~/components/sx/videoSequence/FirstImage.vue';

const { width: defaultWidth, height: defaultHeight } = useWindowSize();

// Добавляем safe guard для начального состояния
const isWindowSizeReady = computed(() => 
  isFinite(defaultWidth.value) && isFinite(defaultHeight.value)
);

const canvasReady = ref(false);
const totalFrames = 197; // Общее количество кадров
const pageLoaded = ref(false);
const loadedImages = ref([]); // Массив для хранения загруженных изображений
const canvas = ref(null); // Ссылка на канвас
const scrollContainer = ref(null); // Ссылка на контейнер прокрутки

const displayWidth = ref(0);
const displayHeight = ref(0);

let ctx = null; // Контекст канваса
let animationFrameId = null; // ID для requestAnimationFrame
let currentFrame = 0;
let targetFrame = 0;
const showVideo = ref(true);

const lerp = (start, end, alpha) => start + (end - start) * alpha;

// Функция для получения пути к кадру
const getFrameSrc = (index) => {
  const frameNumber = (index - 1).toString().padStart(6, '0');
  return `/frames/196/%20${frameNumber}.webp`
}

// Предзагрузка всех изображений
const preloadImages = async () => {
  const promises = [];
  for (let i = 1; i <= totalFrames; i++) {
    const img = new Image();
    img.src = getFrameSrc(i);
    promises.push(
      new Promise((resolve) => {
        img.onload = () => {
          loadedImages.value[i - 1] = img; // Сохраняем изображение
          resolve();
        };
        img.onerror = () => resolve(); // Игнорируем ошибки
      })
    );
  }
  await Promise.all(promises);
};

const drawImageContain = (
  img,
  ctx,
  canvasWidth,
  canvasHeight
) => {
  const imgRatio = img.width / img.height;
  const canvasRatio = canvasWidth / canvasHeight;

  let drawWidth, drawHeight;

  if (canvasRatio > imgRatio) {
    // холст шире — картинку растягиваем по высоте
    drawHeight = canvasHeight;
    drawWidth = canvasHeight * imgRatio;
  } else {
    // холст уже — растягиваем по ширине
    drawWidth = canvasWidth;
    drawHeight = canvasWidth / imgRatio;
  }

  const offsetX = (canvasWidth - drawWidth) / 2;
  const offsetY = (canvasHeight - drawHeight) / 2;

  ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
};


const drawFrame = (frameIndex) => {
  if (!ctx || !loadedImages.value[frameIndex]) return;

  const img = loadedImages.value[frameIndex];

  const dpr = window.devicePixelRatio || 1;
  const dw = displayWidth.value;
  const dh = displayHeight.value;

  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, canvas.value.width, canvas.value.height);
  ctx.scale(dpr, dpr);

  if (dw < 1200) {
    drawImageContain(img, ctx, dw, dh);
  } else {
    const imgRatio = img.width / img.height;
    const canvasRatio = dw / dh;

    let drawWidth, drawHeight;

    if (canvasRatio > imgRatio) {
      drawWidth = dw;
      drawHeight = dw / imgRatio;
    } else {
      drawHeight = dh;
      drawWidth = dh * imgRatio;
    }

    const offsetX = (dw - drawWidth) / 2;
    const offsetY = (dh - drawHeight) / 2;

    ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
  }
};


// Обновление кадра на основе позиции скролла
const updateFrame = () => {
  const scrollTop = window.scrollY;
  const containerTop = scrollContainer.value.getBoundingClientRect().top + window.scrollY;
  const scrollProgress = Math.max(0, Math.min(1, (scrollTop - containerTop) / (scrollContainer.value.offsetHeight - window.innerHeight)));
  const isAtTop = scrollTop <= 10;
  if (isAtTop && !showVideo.value) {
    showVideo.value = true;
  } else if (!isAtTop && showVideo.value) {
    showVideo.value = false;
  }

  targetFrame = Math.min(totalFrames - 1, Math.floor(scrollProgress * (totalFrames - 1)));

  // Плавное приближение текущего кадра к целевому
  currentFrame = lerp(currentFrame, targetFrame, 0.15); // Можно поиграть с 0.1–0.2

  drawFrame(Math.round(currentFrame));
  animationFrameId = requestAnimationFrame(updateFrame);
};

// Инициализация канваса
const initCanvas = () => {
  if (!canvas.value) return;

  const dpr = window.devicePixelRatio || 1;

  // Получаем размеры окна
  const width = window.innerWidth;
  const height = window.innerHeight;

  // Сохраняем размеры в реактивные переменные
  displayWidth.value = width;
  displayHeight.value = height;

  // Контекст канваса
  ctx = canvas.value.getContext('2d');

  // Устанавливаем CSS размер (видимый)
  canvas.value.style.width = width + 'px';
  canvas.value.style.height = height + 'px';

  // Логический размер с учётом dpr
  canvas.value.width = width * dpr;
  canvas.value.height = height * dpr;

  // Трансформация
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.scale(dpr, dpr);

  const videoEl = document.querySelector('video');
  if (videoEl) {
    videoEl.style.width = width + 'px';
    videoEl.style.height = height + 'px';
  }
};

// Запуск анимации
const startAnimation = () => {
  if (animationFrameId) cancelAnimationFrame(animationFrameId);
  animationFrameId = requestAnimationFrame(updateFrame);
};

// Остановка анимации
const stopAnimation = () => {
  if (animationFrameId) cancelAnimationFrame(animationFrameId);
};

const handleWheel = () => {
  if (!animationFrameId) {
    animationFrameId = requestAnimationFrame(updateFrame);
  }
};

onMounted(async () => {
  scrollContainer.value = document.querySelector('.scroll-container');

  const fallbackEl = document.querySelector('.frame-fallback');
  if (fallbackEl) {
    fallbackEl.style.width = defaultWidth.value + 'px';
    fallbackEl.style.height = defaultHeight.value + 'px';
  }
  initCanvas();

  await preloadImages();
  canvasReady.value = true;

  startAnimation();

  if (defaultWidth.value > 899) {
    window.addEventListener('resize', initCanvas);
    window.addEventListener('wheel', handleWheel, { passive: true });
  }
});

onUnmounted(() => {
  stopAnimation();
  window.removeEventListener('wheel', handleWheel);
  window.removeEventListener('resize', initCanvas);
});
</script>

<template>
  <div class="sequence">
    <template v-if="defaultWidth > 899">
      <FirstImage v-show="!canvasReady && !showVideo" class="frame-fallback" />
      <canvas v-show="canvasReady && !showVideo" ref="canvas" class="frame-canvas" />
      <video
        v-show="showVideo"
        ref="video"
        muted
        autoplay
        loop
        playsinline
      >
        <source src="/frames/flags.webm" type="video/webm">
      </video>
    </template>
    <template v-else>
      <FirstImage class="frame-fallback--mobile" :style="{ height: `${defaultHeight}px` }" />
    </template>
  </div>
  <div class="scroll-container" />
</template>

<style scoped lang="scss">
.frame-fallback {
  width: 100%;
  height: 100vh;
  display: block;
  object-fit: cover;
  z-index: 1;
  pointer-events: none;
  &--mobile {
    width: 100%;
    display: block;
    object-fit: cover;
    z-index: 1;
    pointer-events: none;
  }
}

.sequence {
  position: absolute;
  width: 100%;
  overflow: hidden;
  @media (max-width: 899px) {
    position: relative;
  }
  img {
    object-fit: cover;
    height: auto !important;
    width: 100%;
    @media (max-width: 1200px) {
      object-fit: contain;
    }
  }
}

.scroll-container {
  min-height: 200vh; /* Увеличиваем высоту для прокрутки */
  width: 100%;
  position: absolute;
  top: 0;
  overflow: hidden;
  @media (max-width: 1200px) {
    min-height: 160vh;
  }
  @media (max-width: 899px) {
    display: none;
  }
}

.frame-canvas {
  width: 100%;
  height: 100vh; /* Полная высота видимой области */
  display: block;
  &.hidden {
    opacity: 0;
  }
}

video {
  width: 100%;
  height: 100vh;
  object-fit: cover;
  @media (max-width: 1200px) {
    object-fit: contain;
  }
}
</style>
